import React, { MouseEvent, useContext, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import TranslateIcon from '@mui/icons-material/Translate';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useLanguageQuery } from '../../../graphql/query/language/useLanguageQuery';
import Context from '../../context';
import useNotification from '../_hook/useNotification';
import { useUpdateUserSettingMutation } from '../../../graphql/mutation/user/updateUserSettingMutation';
import useLanguage from '../_hook/useLanguage';
import useCookie from '../_hook/useCookie';
import { COOKIE_KEY } from '../constant/cookie';

const useStyles = makeStyles((theme) => ({
  button: {
    borderRadius: '50px',
  },
  buttonSubtitle: {
    fontSize: '12px',
  },
  iconArrowUp: {
    color: '#1D7EA1',
    fontSize: '24px !important',
  },
  iconArrowDown: {
    fontSize: '24px !important',
  },
  menuItem: {
    height: '48px',
  },
  mainButton: {
    padding: 0,
    minWidth: 0,
    textTransform: 'uppercase',
  },
  nested: { paddingLeft: theme.spacing(9) },
}));

interface IProps {
  useMenuItem?: boolean;
}

export default function HeaderLanguageButton(props: IProps) {
  const classes = useStyles();
  const { useMenuItem } = props;
  const { cookies, setCookie } = useCookie();
  const { user } = useContext(Context);
  const { getText } = useLanguage();
  const [isOpenMenuItemCollapse, setIsOpenMenuItemCollapse] = useState(false);
  const { notification } = useNotification();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { t, i18n } = useTranslation();
  const isMenuOpen = Boolean(anchorEl);

  const { data } = useLanguageQuery(
    `
      _id
      code
      name {
        text
        la
      }
  `,
    {
      variables: {
        ...{
          filter: {
            status: true,
          },
          sort: {
            primary: -1,
            status: -1,
            code: 1,
          },
        },
      },
      fetchPolicy: 'cache-first',
    }
  );

  const [updateUserSettingMutation] = useUpdateUserSettingMutation(
    `updatedUser {
				_id
				profile {
					settings {
                      company
                      language
                      country
                      currency
                    }
				}
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const languages = data && data.languages ? data.languages.items : [];

  const handleMenuOpen = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  function onClickLanguage(newLanguageCode: string) {
    setCookie(COOKIE_KEY.LANGUAGE, newLanguageCode);
    // @ts-ignore
    i18n.changeLanguage(newLanguageCode);
    updateUserSettingMutation({
      variables: {
        _id: user._id,
        language: newLanguageCode,
        country: cookies[COOKIE_KEY.COUNTRY],
        currency: cookies[COOKIE_KEY.CURRENCY],
      },
    });
    handleMenuClose();
    setIsOpenMenuItemCollapse(false);
  }

  const menuId = 'language-menu';

  const menuItems = languages.map((language) => (
    <MenuItem
      key={language.code}
      className={classes.menuItem}
      selected={cookies[COOKIE_KEY.LANGUAGE] === language.code}
      onClick={() => {
        onClickLanguage(language.code);
      }}
    >
      {getText(language.name)}
    </MenuItem>
  ));
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {menuItems}
    </Menu>
  );

  const renderDesktopIconButton = (
    <Button
      className={classes.mainButton}
      aria-haspopup="true"
      variant="text"
      color="inherit"
      onClick={handleMenuOpen}
      endIcon={
        anchorEl ? (
          <ArrowDropUpIcon className={classes.iconArrowUp} />
        ) : (
          <ArrowDropDownIcon className={classes.iconArrowDown} />
        )
      }
    >
      {cookies[COOKIE_KEY.LANGUAGE]}
    </Button>
  );

  return (
    <>
      {useMenuItem ? (
        <>
          <MenuItem
            className={classes.menuItem}
            onClick={() => setIsOpenMenuItemCollapse(!isOpenMenuItemCollapse)}
          >
            <ListItemIcon style={{ minWidth: '40px' }}>
              <TranslateIcon />
            </ListItemIcon>
            <ListItemText
              primary={`${t('language')} (${
                cookies[COOKIE_KEY.LANGUAGE]
                  ? cookies[COOKIE_KEY.LANGUAGE].toUpperCase()
                  : cookies[COOKIE_KEY.LANGUAGE]
              })`}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="collapse"
                onClick={() =>
                  setIsOpenMenuItemCollapse(!isOpenMenuItemCollapse)
                }
              >
                {isOpenMenuItemCollapse ? (
                  <ArrowDropUpIcon />
                ) : (
                  <ArrowDropDownIcon />
                )}
              </IconButton>
            </ListItemSecondaryAction>
          </MenuItem>
          <Collapse in={isOpenMenuItemCollapse} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {languages.map((language) => (
                <ListItem
                  key={language.code}
                  selected={cookies[COOKIE_KEY.LANGUAGE] === language.code}
                  button
                  className={classes.nested}
                  onClick={() => onClickLanguage(language.code)}
                >
                  <ListItemText primary={getText(language.name)} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      ) : (
        <Grid item>
          <Grid item>
            <Typography variant="body1" className={classes.buttonSubtitle}>
              {t('language')}
            </Typography>
          </Grid>
          <Grid item>{renderDesktopIconButton}</Grid>
        </Grid>
      )}
      {renderMenu}
    </>
  );
}
